import { BinaryWriter, common } from '@neo-one/client-common';
import { utils, } from '@neo-one/node-core';
import { BLOCK_HEIGHT_MAX_SIZE_CHECKS, MAX_SIZE_BIG_INTEGER } from '../constants';
import { IntegerTooLargeError, InvalidRecursiveSerializeError, InvalidValueAccountError, InvalidValueArrayError, InvalidValueAssetError, InvalidValueAttributeError, InvalidValueAttributeStackItemError, InvalidValueBlockBaseError, InvalidValueBlockError, InvalidValueContractError, InvalidValueEnumeratorError, InvalidValueHeaderError, InvalidValueInputError, InvalidValueIteratorError, InvalidValueMapStackItemError, InvalidValueOutputError, InvalidValueStorageContextStackItemError, InvalidValueTransactionError, InvalidValueValidatorError, InvalidValueWitnessError, } from './errors';
import { StackItemType } from './StackItemType';
export class StackItemBase {
    constructor() {
        this.mutableCount = 0;
    }
    get referenceCount() {
        return this.mutableCount;
    }
    increment(seen = new Set()) {
        if (seen.has(this)) {
            return 1;
        }
        seen.add(this);
        this.mutableCount += 1;
        if (this.mutableCount > 1) {
            return 1;
        }
        return this.incrementInternal(seen) + 1;
    }
    decrement(seen = new Set()) {
        if (seen.has(this)) {
            return -1;
        }
        seen.add(this);
        this.mutableCount -= 1;
        if (this.mutableCount >= 1) {
            return -1;
        }
        return this.decrementInternal(seen) - 1;
    }
    toStructuralKey() {
        return this.asBuffer().toString('hex');
    }
    equals(other) {
        if (other === undefined) {
            return false;
        }
        if (this === other) {
            return true;
        }
        if (other instanceof StackItemBase) {
            const thisValue = this.asBufferMaybe();
            const otherValue = other.asBufferMaybe();
            return thisValue !== undefined && otherValue !== undefined && thisValue.equals(otherValue);
        }
        return false;
    }
    serialize(seen = new Set()) {
        if (seen.has(this)) {
            throw new InvalidRecursiveSerializeError();
        }
        const nextSeen = new Set(seen);
        nextSeen.add(this);
        return this.serializeInternal(nextSeen);
    }
    asArray() {
        throw new InvalidValueArrayError();
    }
    asBigInteger(currentBlockIndex) {
        if (currentBlockIndex === undefined || currentBlockIndex < BLOCK_HEIGHT_MAX_SIZE_CHECKS) {
            return this.asBigIntegerUnsafe();
        }
        const value = this.asBuffer();
        if (value.length > MAX_SIZE_BIG_INTEGER) {
            throw new IntegerTooLargeError();
        }
        return utils.fromSignedBuffer(value);
    }
    asBigIntegerUnsafe() {
        return utils.fromSignedBuffer(this.asBuffer());
    }
    asBuffer() {
        throw new Error('Unimplemented.');
    }
    asBufferMaybe() {
        try {
            return this.asBuffer();
        }
        catch {
            return undefined;
        }
    }
    asBoolean() {
        return this.asBuffer().some((value) => value !== 0);
    }
    asUInt160() {
        return common.bufferToUInt160(this.asBuffer());
    }
    asUInt160Maybe() {
        try {
            return this.asUInt160();
        }
        catch {
            return undefined;
        }
    }
    asUInt256() {
        return common.bufferToUInt256(this.asBuffer());
    }
    asUInt256Maybe() {
        try {
            return this.asUInt256();
        }
        catch {
            return undefined;
        }
    }
    asECPoint() {
        return common.bufferToECPoint(this.asBuffer());
    }
    asECPointMaybe() {
        try {
            return this.asECPoint();
        }
        catch {
            return undefined;
        }
    }
    asString() {
        return utils.toUTF8(this.asBuffer());
    }
    asHeader() {
        throw new InvalidValueHeaderError();
    }
    asBlockBase() {
        throw new InvalidValueBlockBaseError();
    }
    asBlock() {
        throw new InvalidValueBlockError();
    }
    asTransaction() {
        throw new InvalidValueTransactionError();
    }
    asWitness() {
        throw new InvalidValueWitnessError();
    }
    asAttribute() {
        throw new InvalidValueAttributeError();
    }
    asAttributeStackItem() {
        throw new InvalidValueAttributeStackItemError();
    }
    asInput() {
        throw new InvalidValueInputError();
    }
    asOutput() {
        throw new InvalidValueOutputError();
    }
    asAccount() {
        throw new InvalidValueAccountError();
    }
    asAsset() {
        throw new InvalidValueAssetError();
    }
    asContract() {
        throw new InvalidValueContractError();
    }
    asValidator() {
        throw new InvalidValueValidatorError();
    }
    asMapStackItem() {
        throw new InvalidValueMapStackItemError();
    }
    asEnumerator() {
        throw new InvalidValueEnumeratorError();
    }
    asIterator() {
        throw new InvalidValueIteratorError();
    }
    asStorageContextStackItem(_options) {
        throw new InvalidValueStorageContextStackItemError();
    }
    isArray() {
        return false;
    }
    isMap() {
        return false;
    }
    toContractParameter() {
        throw new Error('Not Implemented');
    }
    get size() {
        return this.asBuffer().length;
    }
    toString() {
        return JSON.stringify(this.convertJSON());
    }
    convertJSON(seen = new Set()) {
        if (seen.has(this)) {
            return '<circular>';
        }
        const nextSeen = new Set(seen);
        nextSeen.add(this);
        return this.convertJSONInternal(nextSeen);
    }
    incrementInternal(_seen) {
        return 0;
    }
    decrementInternal(_seen) {
        return 0;
    }
    incrementInternalArray(stackItems, seen) {
        return stackItems.reduce((acc, val) => acc + val.increment(seen), 0);
    }
    decrementInternalArray(stackItems, seen) {
        return stackItems.reduce((acc, val) => acc + val.decrement(seen), 0);
    }
    serializeInternal(_seen) {
        const writer = new BinaryWriter();
        writer.writeUInt8(StackItemType.ByteArray);
        writer.writeVarBytesLE(this.asBuffer());
        return writer.toBuffer();
    }
    convertJSONInternal(_seen) {
        try {
            return this.asBuffer().toString('hex');
        }
        catch {
            return 'UNKNOWN';
        }
    }
}

//# sourceMappingURL=StackItemBase.js.map
